import React, {
	createContext,
	useContext,
	useRef,
	useMemo,
	useCallback,
	useState,
	useEffect,
} from 'react';
import { useAuth } from 'react-oidc-context';
import useAuthenticated from './useAuthenticated';

export type SilentRefreshFn = (options?: {
	signal?: AbortSignal;
}) => Promise<import('oidc-client-ts').User | null>;

export type SilentContext = [boolean, SilentRefreshFn];

const silentContext = createContext<SilentContext>(null!);

export function useOidcSilent(): SilentContext {
	return useContext(silentContext);
}

/**
 * Silent signin provider provides loading feedback on the initial silent signin
 */
export function OidcSilentProvider(props: { children: React.ReactNode }) {
	const { children } = props;
	const [isSilentLoading, setSilentLoading] = useState(true);
	const { isLoading, signinSilent } = useAuth();
	const isAuthenticated = useAuthenticated();

	const refreshPromise = useRef<ReturnType<typeof signinSilent> | undefined>(
		undefined,
	);

	const silentRefresh = useCallback<SilentRefreshFn>(
		async ({ signal } = {}) => {
			if (signal?.aborted) return null;

			// Group silentRefresh calls
			if (refreshPromise.current) return refreshPromise.current;

			const promise = signinSilent();
			refreshPromise.current = promise;

			// Clean up only once
			let isClean = false;
			const cleanup = () => {
				if (isClean) return;
				isClean = true;
				refreshPromise.current = undefined;
				setSilentLoading(false);
			};

			signal?.addEventListener('abort', cleanup);

			// Clean up states
			promise.catch(() => null).then(cleanup);

			return promise;
		},
		[signinSilent],
	);

	// Try a silent signin on load
	const silentStatus = useRef(false);
	useEffect(() => {
		const controller = new AbortController();

		if (isLoading) return undefined;

		// Call it just once
		if (silentStatus.current) return undefined;
		silentStatus.current = true;

		// Skip all together if we good
		if (isAuthenticated) {
			setSilentLoading(false);
			return undefined;
		}

		silentRefresh({ signal: controller.signal });

		return () => {
			controller.abort();
		};
	}, [isLoading, isAuthenticated, silentRefresh]);

	const value = useMemo<SilentContext>(
		() => [isSilentLoading, silentRefresh],
		[isSilentLoading, silentRefresh],
	);

	return (
		<silentContext.Provider value={value}>{children}</silentContext.Provider>
	);
}

export default OidcSilentProvider;
